/* eslint-disable @typescript-eslint/no-explicit-any */
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { makeTranslateService } from '../../data/services/common/translate/translate-service-factory';
import { LanguageKey } from '../../main/translate/language-key/language-key';
import { LocationId } from '../../main/translate/location-id/location-id';
import { AppMenuitemComponent } from './app.menuitem.component';
import { LayoutService } from './service/app.layout.service';
import { RouteAccessService } from '../permissions/route-access.service';

interface AppMenuEntry {
  label: string;
  icon?: string;
  routerLink?: string[];
  items?: AppMenuEntry[];
}

@Component({
  selector: 'app-menu',
  templateUrl: './app.menu.component.html',
  standalone: true,
  imports: [CommonModule, AppMenuitemComponent],
})
export class AppMenuComponent implements OnInit {
  public model: any[] = [];
  public translateService = makeTranslateService();
  public languageKey = LanguageKey;

  constructor(
    public layoutService: LayoutService,
    private readonly routeAccessService: RouteAccessService,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    void this.buildMenu();
  }

  private async buildMenu(): Promise<void> {
    const model = [
      {
        label: 'Home',
        items: [
          {
            label: this.translateService.translateKey(LanguageKey.DASHBOARD),
            icon: 'pi pi-fw pi-home',
            routerLink: ['/home'],
          },
          {
            label: this.translateService.translateKey(LanguageKey.APPOINTMENTS),
            icon: 'pi pi-fw pi-calendar',
            routerLink: ['/appointments'],
          },
        ],
      },
      {
        label: this.translateService.translateKey(LanguageKey.RECORDS),
        icon: 'pi pi-fw pi-briefcase',
        items: [
          {
            label: this.translateService.translateKey(LanguageKey.CLIENTS),
            icon: 'pi pi-fw pi-users',
            routerLink: ['/clients'],
          },
          {
            label: 'Recursos',
            icon: 'pi pi-fw pi-building',
            routerLink: ['/resources'],
          },
          {
            label: this.translateService.translateKey(LanguageKey.SERVICES),
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/services'],
          },
          {
            label: 'Configuração da agenda',
            icon: 'pi pi-fw pi-cog',
            routerLink: ['/schedule-settings'],
          },
        ],
      },
    ];

    const visibleSections = await Promise.all(
      model.map(async (section) => ({
        ...section,
        items: await this.filterVisibleItems(section.items),
      })),
    );

    this.model = visibleSections.filter((section) => section.items.length > 0);
    this.changeDetectorRef.detectChanges();
  }

  private async filterVisibleItems(
    items: AppMenuEntry[],
  ): Promise<AppMenuEntry[]> {
    const visibleItems = await Promise.all(
      items.map(async (item) => {
        const routeUrl = item.routerLink?.[0];
        const hasPermission = routeUrl
          ? await this.routeAccessService.canAccessRoute(routeUrl)
          : true;

        if (!hasPermission) {
          return null;
        }

        if (!item.items?.length) {
          return item;
        }

        const children = await this.filterVisibleItems(item.items);

        if (!children.length) {
          return null;
        }

        return {
          ...item,
          items: children,
        };
      }),
    );

    return visibleItems.filter((item): item is AppMenuEntry => item !== null);
  }

  async changeLanguage(location: LocationId) {
    await this.translateService.init(location);
  }
}
